// Ch83 · reading the inbound-SMS measurement, as a DIAGNOSIS not a count.
//
// admin_truth.messages.inbound_sms answers a question nothing in this estate
// could: are renters' replies actually landing? The Leads funnel reports
// "contacted 5 / replied 0", and that zero has two readings that need opposite
// responses — nobody replied (copy and timing), or replies are not arriving (a
// broken pipe). Handing an operator "0" makes them guess between the two.
//
// So this turns the payload into a sentence. The distinction lives here, in a
// pure function, because it is the part worth testing: the card only draws it.

export interface InboundReplies {
  total: number;
  within_7d: number;
  within_30d: number;
  distinct_clients_who_replied: number;
  latest_at: string | null;
  hours_since_latest: number | null;
  unreadable_timestamps: number;
  outbound_total: number;
}

export type InboundTone = "ok" | "warn" | "bad" | "unknown";

export interface InboundReading {
  tone: InboundTone;
  /** The diagnosis, in one line. Never a bare number. */
  headline: string;
  /** The figures behind it, for someone who wants to check the reasoning. */
  detail: string;
}

const HOURS_PER_DAY = 24;
/** Beyond this, "quiet" stops being a plausible reading of silence. */
const STALE_DAYS = 30;
/** Replies inside this window mean the pipe is demonstrably working. */
const FRESH_HOURS = 72;

export function readInbound(value: InboundReplies | null | undefined): InboundReading {
  if (!value) {
    return {
      tone: "unknown",
      headline: "The message ledger could not be read",
      // Not "0 replies". A failed read and a silent month are different facts
      // and only one of them is about the business.
      detail: "This is a failed measurement, not a measurement of silence.",
    };
  }

  const days = value.hours_since_latest == null ? null : value.hours_since_latest / HOURS_PER_DAY;
  const detail =
    `${value.within_7d} in 7 days · ${value.within_30d} in 30 · ` +
    `${value.distinct_clients_who_replied} people have ever replied · ` +
    `${value.outbound_total} sent` +
    (value.unreadable_timestamps ? ` · ${value.unreadable_timestamps} undated` : "");

  if (value.total === 0) {
    // The alarming case, and the one the funnel's "replied 0" was hiding: we
    // are texting people and NOTHING is coming back. Either the webhook stopped
    // writing or nobody answers us, and both are worth someone's morning.
    if (value.outbound_total > 0) {
      return {
        tone: "bad",
        headline: `${value.outbound_total} texts sent, not one reply ever received`,
        detail,
      };
    }
    return {
      tone: "warn",
      headline: "No texts either way yet — nothing to conclude",
      detail,
    };
  }

  if (days !== null && days > STALE_DAYS) {
    return {
      tone: "bad",
      headline: `No reply in ${Math.round(days)} days`,
      detail,
    };
  }
  if (value.hours_since_latest !== null && value.hours_since_latest <= FRESH_HOURS) {
    return {
      tone: "ok",
      headline: "Replies are landing",
      detail,
    };
  }
  return {
    tone: "warn",
    headline: days === null ? "Replies exist, but none is dated" : `Last reply ${Math.round(days)} days ago`,
    detail,
  };
}
